
import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentUser } from '@/contexts/UserContext'
import { useComponentAccess } from '@/lib/rbac/hooks'
import { COMPONENTS } from '@/lib/rbac/constants'
import { ObjetivosEstrategicosTab } from '@/components/ObjetivosEstrategicosTab'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs'
import { ProfileHeader } from '@/components/ProfileHeader'
import { VacationAlerts } from '@/components/VacationAlerts'
import { GoalsGrid } from '@/components/GoalsGrid'
import { TeamOrgChart } from '@/components/TeamOrgChart'
import { DepartmentMetrics } from '@/components/DepartmentMetrics'
import { PerformanceTab } from '@/components/PerformanceTab'
import { AcompanhamentoTab } from '@/components/AcompanhamentoTab'
import { ConfiguracoesTab } from '@/components/ConfiguracoesTab'
import { VacationAlert, TeamMember } from '@/types/dashboard'
import { getRealEmployeeVacationData, calculateDashboardVacationAlerts } from '@/lib/dashboard-utils'
import {
  getAllMonumentaEmployees,
  getDashboardConfigForEmployee,
  getAllSubordinatesInHierarchy,
  caseInsensitiveEquals,
  generateUserGoals,
  formatCurrency as realFormatCurrency,
  type MonumentaEmployee
} from '@/lib/real-data-service'
import { X, Mail, Users } from 'lucide-react'

// 🚀 DYNAMIC ORGANIZATIONAL DASHBOARD - Adapts to ANY Monumenta employee
export function EnhancedHomeScreen() {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPerformanceCategory, setSelectedPerformanceCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState<MonumentaEmployee[]>([]);
  const [vacationData, setVacationData] = useState<any[]>([]);
  const [dashboardConfig, setDashboardConfig] = useState<any>(null);
  const [goals, setGoals] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 🔐 AUTH INTEGRATION - Get authenticated user for role-based filtering
  const { user: authUser } = useAuth();

  // 🔄 CURRENT USER - Get current active user (could be switched user)
  const { currentUser } = useCurrentUser();

  // 🔐 RBAC INTEGRATION - Check strategic objectives access
  const hasStrategicObjectivesAccess = useComponentAccess(COMPONENTS.STRATEGIC_OBJECTIVES, 'view');

  // Load data asynchronously
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load all data asynchronously
        const [employeesData, vacationDataResult, dashboardConfigResult] = await Promise.all([
          getAllMonumentaEmployees(),
          currentUser?.employee ? getRealEmployeeVacationData(currentUser.employee as any) : Promise.resolve([]),
          currentUser?.employee ? getDashboardConfigForEmployee(currentUser.employee) : Promise.resolve(null)
        ]);

        setEmployees(employeesData);
        setVacationData(vacationDataResult);
        setDashboardConfig(dashboardConfigResult);

        // Load goals if user is available
        if (currentUser) {
          const userGoals = await generateUserGoals(currentUser);
          setGoals(userGoals);
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  // Note: currentUser is now from useCurrentUser() hook above

  // 🚨 VACATION ALERTS - Calculate alerts for current manager's team using real data
  const vacationAlerts = useMemo(() => {
    if (vacationData.length === 0) return [];
    return calculateDashboardVacationAlerts(vacationData);
  }, [vacationData]);

  // 🏢 FLATTENED HIERARCHY VACATION DATA - Get all subordinates in flat format for vacation balance table
  const [hierarchyVacationData, setHierarchyVacationData] = useState<any[]>([]);

  useEffect(() => {
    if (!currentUser || employees.length === 0) {
      setHierarchyVacationData([]);
      return;
    }

    const loadHierarchyVacationData = async () => {
      try {
        // Get all subordinates from hierarchy (flattened)
        const allSubordinates = await getAllSubordinatesInHierarchy(currentUser.name);

        // Convert to vacation data format matching TECH_TEAM_VACATION structure
        const vacationData = allSubordinates.map((employee: any) => {
        // Find matching employee in loaded employees data to get vacation balance
        const employeeData = employees.find(emp =>
          caseInsensitiveEquals(emp.name, employee.name)
        );

        const vacationBalance = employeeData?.vacationBalance || 0;
        const daysUsed = Math.max(0, 30 - vacationBalance); // Assuming 30 days annual allowance

          return {
            name: employee.name,
            daysAvailable: 30,
            daysUsed: daysUsed,
            daysRemaining: vacationBalance,
            period: '2024',
            department: employee.department,
            role: employee.role,
            saldoReal: vacationBalance
          };
        });

        setHierarchyVacationData(vacationData);
      } catch (err) {
        console.error('Error getting hierarchy vacation data:', err);
        setHierarchyVacationData([]);
      }
    };

    loadHierarchyVacationData();
  }, [currentUser, employees]);

  // 🚨 SAFETY CHECKS: Ensure we have required data before rendering
  if (!currentUser || !dashboardConfig) {
    return (
      <div className="p-6 space-y-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-yellow-500 text-2xl"></span>
            </div>
            <p className="text-yellow-400 font-body mb-2">Configuração não encontrada</p>
            <p className="text-brand-gray-light font-body text-sm">Usuário ou configuração do dashboard não disponível</p>
          </div>
        </div>
      </div>
    );
  }

  // TypeScript assertions: We know these are not null due to validation above
  const user = currentUser;
  const config = dashboardConfig;

  // 📊 PERSONALIZED PROFILE - Based on real employee data from Excel
  const profile = {
    name: user.name,
    title: user.role,
    department: user.employee?.currentEngagement?.departmentName || '',
    email: user.email,
    phone: user.employee?.personalInfo?.phone || '(11) 99999-0000', // Default phone
    location: 'São Paulo, SP', // Default location
    // Real metrics from organizational data
    totalEmployees: employees.length, // Total employees from loaded data
    departmentBudget: config.departmentMetrics?.totalCost || 0,
    teamSize: config.directReports.length,
    isManager: config.isManager,
    directReports: config.directReports,
    departmentEmployeeCount: config.departmentMetrics?.employeeCount || 1
  };

  // Goals are now loaded asynchronously in useEffect

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-6 space-y-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-brand-gray-light font-body">Carregando dados do dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6 space-y-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl"></span>
            </div>
            <p className="text-red-400 font-body mb-2">Erro ao carregar dados</p>
            <p className="text-brand-gray-light font-body text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary mt-4"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }



  const formatCurrency = realFormatCurrency;

  const handleMemberClick = (member: TeamMember) => {
    setSelectedMember(member);
    setIsDialogOpen(true);
  };

  const handlePerformanceCategoryClick = (category: string) => {
    // Toggle filter - se já está selecionada, remove o filtro
    setSelectedPerformanceCategory(selectedPerformanceCategory === category ? null : category);
  };



  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">{profile.title}</h1>
          <p className="text-brand-gray-light font-body text-lg">Gestão de {profile.department} • Painel personalizado de acompanhamento</p>
        </div>
      </div>

      {/* Enhanced Tabs */}
      <Tabs defaultValue="resumo">
        <div className="bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 shadow-monumenta border border-gray-800/50 mb-8">
          <div className="flex space-x-1 overflow-x-auto">
            <TabsTrigger value="resumo">Resumo</TabsTrigger>
            <TabsTrigger value="desempenho">Desempenho</TabsTrigger>
            <TabsTrigger value="acompanhamento">Acompanhamento</TabsTrigger>
            {/* Objetivos Estratégicos tab - only for technical directors */}
            {hasStrategicObjectivesAccess && (
              <TabsTrigger value="objetivos">Objetivos Estratégicos</TabsTrigger>
            )}
            <TabsTrigger value="configuracoes">Configurações</TabsTrigger>
          </div>
        </div>

        <TabsContent value="resumo">
          <div className="space-y-6 animate-fade-in-up">
            {/* Profile Header */}
            <ProfileHeader profile={profile} />

            {/* Vacation Alerts */}
            <VacationAlerts vacationAlerts={vacationAlerts} />

            {/* Goals Grid */}
            <GoalsGrid goals={goals} />

            {/* Team Org Chart */}
            <TeamOrgChart 
              isManager={config.isManager}
              directReports={config.directReports}
              user={user.employee as any}
                    onMemberClick={handleMemberClick}
                  />

            {/* Department Metrics */}
            <DepartmentMetrics 
              hierarchyVacationData={hierarchyVacationData}
              formatCurrency={formatCurrency}
            />

            {/* Dialog para detalhes do membro */}
            {isDialogOpen && selectedMember && (
              <div className="modal-overlay">
                <div className="modal-content max-w-md animate-scale-in">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-display text-xl font-bold text-white">Detalhes do Colaborador</h3>
                    <button 
                      onClick={() => setIsDialogOpen(false)}
                      className="btn-icon text-gray-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center text-white text-lg font-bold">
                        {selectedMember.avatar}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{selectedMember.name}</h3>
                        <p className="text-lg text-blue-400">{selectedMember.role}</p>
                        <p className="text-sm text-gray-400">{selectedMember.team}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300">{selectedMember.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300">{selectedMember.directReports} pessoas sob gestão</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="desempenho">
          <PerformanceTab 
            selectedPerformanceCategory={selectedPerformanceCategory}
            onPerformanceCategoryClick={handlePerformanceCategoryClick}
          />
        </TabsContent>

        <TabsContent value="acompanhamento">
          <AcompanhamentoTab />
        </TabsContent>

        <TabsContent value="configuracoes">
          <ConfiguracoesTab />
        </TabsContent>

        {/* Objetivos Estratégicos content - only for technical directors */}
        {hasStrategicObjectivesAccess && (
          <TabsContent value="objetivos">
            <ObjetivosEstrategicosTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}